import React from "react";
import {
  TextField,
  Chip,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

/**
 * FreeTextChipsInput
 * Props:
 *  - value: string[]            // valori correnti (controllato)
 *  - onChange: (string[]) => {} // callback con l'array aggiornato
 *  - label?: string
 *  - placeholder?: string
 *  - disabled?: boolean
 *  - margin?: 'none' | 'dense' | 'normal'
 *  - required?: boolean
 *  - error?: boolean
 *  - helperText?: string
 *  - maxItems?: number          // limite elementi (opzionale)
 *  - maxLength?: number         // limite caratteri per item (opzionale)
 *  - dedupeCaseInsensitive?: boolean (default true)
 *  - splitOnPaste?: boolean     // split su , ; newline (default true)
 *  - delimiters?: RegExp        // default: /[,\n;]+/
 *  - normalize?: (s: string) => string // normalizzazione personalizzata
 */
export default function FreeTextChipsInput({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  margin = "dense",
  required,
  error,
  helperText,
  maxItems,
  maxLength,
  dedupeCaseInsensitive = true,
  splitOnPaste = true,
  delimiters = /[,\n;]+/,
  normalize,
}) {
  const [inputValue, setInputValue] = React.useState("");

  // Memo per evitare warning sui useCallback che dipendono da 'current'
  const current = React.useMemo(() => (Array.isArray(value) ? value : []), [
    value,
  ]);

  const norm = React.useCallback(
    (s) => {
      const base =
        typeof normalize === "function"
          ? normalize(s)
          : s.trim().replace(/\s+/g, " ");
      const cut =
        typeof maxLength === "number" ? base.slice(0, maxLength) : base;
      return cut;
    },
    [normalize, maxLength]
  );

  const dedupe = React.useCallback(
    (arr) => {
      if (!dedupeCaseInsensitive) {
        return Array.from(new Set(arr));
      }
      const seen = new Set();
      const out = [];
      for (const it of arr) {
        const key = it.toLocaleLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          out.push(it);
        }
      }
      return out;
    },
    [dedupeCaseInsensitive]
  );

  const applyMaxItems = React.useCallback(
    (arr) => (typeof maxItems === "number" ? arr.slice(0, maxItems) : arr),
    [maxItems]
  );

  const emit = React.useCallback(
    (arr) => {
      const cleaned = dedupe(arr.map(norm).filter((s) => s.length > 0));
      onChange && onChange(applyMaxItems(cleaned));
    },
    [onChange, norm, dedupe, applyMaxItems]
  );

  const tryAddInput = React.useCallback(() => {
    const v = norm(inputValue);
    if (!v) return;
    if (typeof maxItems === "number" && current.length >= maxItems) return;
    emit([...current, v]);
    setInputValue("");
  }, [inputValue, norm, current, emit, maxItems]);

  const handleKeyDown = (e) => {
    if (disabled) return;
    // Aggiunta su Enter, virgola, punto e virgola
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      tryAddInput();
    }
  };

  const handlePaste = (e) => {
    if (!splitOnPaste) return;
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    if (!delimiters.test(text)) return;
    e.preventDefault();
    const parts = text.split(delimiters).map(norm).filter(Boolean);
    if (parts.length === 0) return;
    const merged = [...current, ...parts];
    emit(merged);
    setInputValue("");
  };

  return (
    <FormControl fullWidth margin={margin} required={required} error={error}>
      <Autocomplete
        multiple
        freeSolo
        options={[]} // nessuna opzione: testi liberi
        value={current}
        onChange={(_, newValue) => {
          // aggiunte/rimozioni gestite dall'Autocomplete
          emit(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(_, v /* reason */) => setInputValue(v)}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Chip
                key={`${option}-${index}`}
                size="small"
                label={option}
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="standard"
            disabled={disabled}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        )}
        disabled={disabled}
        filterSelectedOptions
        clearOnBlur
        handleHomeEndKeys
      />
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}
